import type { TransactionContext } from "../transaction-context";
import type {
  AutomaticBillingOfferStatus,
  FastWebAutoTopUpConsentVersion,
} from "./types/automatic-offers";
import type {
  BillingJsonValue,
  EffectiveSubscription,
  SetPostpaidBillingInput,
  StripeEvent,
  StripeEventStatus,
  SubscriptionTier,
} from "./types";

/**
 * Stripe API version used by every Stripe SDK client in billing.
 *
 * Keeping this source-controlled prevents local webhook/event shapes and
 * checkout/session writes from silently following a Stripe account default.
 */
export const STRIPE_API_VERSION = "2026-05-27.dahlia";

export type StripeCatalogSubscriptionTier = Exclude<
  SubscriptionTier,
  "enterprise"
>;

/**
 * Commercial Stripe subscription identities. These remain distinct from
 * platform entitlement tiers: Operator and Studio both retain Free limits.
 */
export const STRIPE_SUBSCRIPTION_PLANS = {
  FREE: "free",
  GROWTH: "growth",
  OPERATOR: "operator",
  STUDIO: "studio",
} as const;

export type StripeSubscriptionPlanKey =
  (typeof STRIPE_SUBSCRIPTION_PLANS)[keyof typeof STRIPE_SUBSCRIPTION_PLANS];

export type StripeCatalogCurrency = "usd";
export type StripeCatalogRecurringInterval = "month";

export interface StripeCatalogSubscriptionPrice {
  unitAmountCents: number | null;
  currency: StripeCatalogCurrency;
  interval: StripeCatalogRecurringInterval;
}

export interface StripeCatalogSubscriptionIntroductoryDiscount {
  amountOffCents: number;
  duration: "once";
}

export interface StripeCatalogSubscriptionEntry {
  planKey: StripeSubscriptionPlanKey;
  entitlementTier: StripeCatalogSubscriptionTier;
  recurringAllowanceCents: number;
  introductoryDiscount: StripeCatalogSubscriptionIntroductoryDiscount | null;
  lookupKey: string;
  productName: string;
  productDescription: string;
  price: StripeCatalogSubscriptionPrice;
  trialPeriodDays: number | null;
  metadata: {
    internalTier: StripeCatalogSubscriptionTier;
  };
}

export interface StripeCatalogCreditCustomUnitAmount {
  enabled: boolean;
  minimumCents: number | null;
  maximumCents: number | null;
}

export interface StripeCatalogCreditEntry {
  lookupKey: string;
  productName: string;
  productDescription: string;
  customUnitAmount: StripeCatalogCreditCustomUnitAmount;
  currency: StripeCatalogCurrency;
  metadata: {
    internalKind: "credits";
  };
}

export interface StripeCatalog {
  subscriptions: Record<
    StripeSubscriptionPlanKey,
    StripeCatalogSubscriptionEntry
  >;
  credits: StripeCatalogCreditEntry;
}

export interface StripeSubscriptionPlanIds {
  productId: string;
  priceId: string;
  archivedPriceIds: string[];
  /**
   * The once-only introductory Coupon attached directly by subscription
   * Checkout. Absent for plans without an introductory discount and for
   * generated account mappings that have not yet been re-synced.
   */
  introCouponId?: string;
}

/** @deprecated Use StripeSubscriptionPlanIds for commercial Stripe plans. */
export type StripeSubscriptionTierIds = StripeSubscriptionPlanIds;

export type StripeAccountSubscriptionIds = Record<
  StripeCatalogSubscriptionTier,
  StripeSubscriptionPlanIds
> &
  Partial<
    Record<
      Exclude<StripeSubscriptionPlanKey, StripeCatalogSubscriptionTier>,
      StripeSubscriptionPlanIds
    >
  >;

export interface StripeCreditIds {
  productId: string;
  priceId: string;
}

export interface StripeAccountIds {
  accountLabel: string;
  subscriptions: StripeAccountSubscriptionIds;
  credits: StripeCreditIds;
  /**
   * Price IDs of subscription tiers that are no longer purchasable (e.g. the
   * retired $25 "starter" plan). These are NOT in `subscriptions` and cannot be
   * checked out, but lifecycle webhooks for subscriptions still on these prices
   * must still resolve so a cancellation correctly drops the team to free and
   * clears the local `stripeSubscriptionId`. A legacy price resolves to the
   * free tier — the removed tier grants nothing beyond free.
   */
  legacySubscriptionPriceIds?: string[];
}

export type StripeIdsByAccountHash = Record<string, StripeAccountIds>;

export type StripeCatalogSyncResourceType =
  | "coupon"
  | "product"
  | "price"
  | "ids_file";

export type StripeCatalogSyncAction =
  | "found"
  | "created"
  | "updated"
  | "archived"
  | "skipped";

export interface StripeCatalogSyncReportItem {
  resourceType: StripeCatalogSyncResourceType;
  action: StripeCatalogSyncAction;
  lookupKey: string | null;
  id: string | null;
  message: string;
}

export interface StripeCatalogSyncResult {
  accountHash: string;
  accountLabel: string;
  dryRun: boolean;
  stripeIds: StripeAccountIds;
  reportItems: StripeCatalogSyncReportItem[];
}

export type StripeCheckoutKind = "standard_subscription" | "credit_purchase";
export type StripeAutoTopUpCheckoutKind = "auto_top_up";
export type StripePostpaidInvoiceCheckoutKind = "postpaid_invoice";

export interface StripeSubscriptionCheckoutMetadata {
  checkoutKind: "standard_subscription";
  teamId: string;
  initiatedByUserId: string;
  /** Canonical commercial plan identity for newly-created Checkout sessions. */
  targetPlan: StripeSubscriptionPlanKey;
  /**
   * Entitlement tier retained on the wire for compatibility with older
   * webhook deliveries and existing Free/Growth subscriptions.
   */
  targetTier: StripeCatalogSubscriptionTier;
  paygMandatoryCreditPurchaseAmountCents?: number;
  /** Present only for the fast-web unified onboarding checkout. */
  onboardingSource?: "fast_web";
  automaticOfferProgram?: "fast_web_welcome_v1";
  automaticOfferDecisionId?: string;
  phoneVerifiedAtCheckout?: boolean;
  continuityKind?: "keep_running" | "pause_when_empty";
  autoTopUpConsentVersion?: FastWebAutoTopUpConsentVersion;
  autoTopUpThresholdNanocredits?: string;
  autoTopUpAmountCents?: number;
  autoTopUpMonthlyLimitCents?: number;
}

export interface StripeCreditPurchaseCheckoutMetadata {
  checkoutKind: "credit_purchase";
  teamId: string;
  initiatedByUserId: string;
  amountCents: number;
  discountCode?: string;
  stripePromotionCodeId?: string;
  originalAmountCents?: number;
  /** Present only for the fast-web unified onboarding checkout. */
  onboardingSource?: "fast_web";
  automaticOfferProgram?: "fast_web_welcome_v1";
  automaticOfferDecisionId?: string;
  phoneVerifiedAtCheckout?: boolean;
  continuityKind?: "keep_running" | "pause_when_empty";
  autoTopUpConsentVersion?: FastWebAutoTopUpConsentVersion;
  autoTopUpThresholdNanocredits?: string;
  autoTopUpAmountCents?: number;
  autoTopUpMonthlyLimitCents?: number;
}

export interface StripeAutoTopUpMetadata {
  checkoutKind: StripeAutoTopUpCheckoutKind;
  teamId: string;
  amountCents: number;
  thresholdNanocredits: string;
  balanceNanocreditsAtTrigger: string;
  autoTopUpExecutionId: string;
}

export interface StripePostpaidInvoiceMetadata {
  checkoutKind: StripePostpaidInvoiceCheckoutKind;
  teamId: string;
  invoiceId: string;
  paymentAttemptId: string;
  amountCents: number;
  amountNanocredits: string;
}

export type StripeCheckoutMetadata =
  | StripeSubscriptionCheckoutMetadata
  | StripeCreditPurchaseCheckoutMetadata
  | StripeAutoTopUpMetadata
  | StripePostpaidInvoiceMetadata;

/**
 * Kinds of Stripe SetupIntent we create. SetupIntents collect and save a
 * payment method for future off-session charges without an immediate payment,
 * so they carry their own metadata namespace (`setupIntentKind`) distinct from
 * the checkout-session `checkoutKind` flows above.
 */
export type StripeSetupIntentKind = "default_payment_method";

/**
 * Metadata stamped on a SetupIntent used to collect a team's default payment
 * method in-app (not via Stripe Checkout). The webhook handler and the
 * server-side completion path both read this back to confirm the SetupIntent
 * belongs to the default-payment-method flow before promoting the saved
 * payment method to the customer's `invoice_settings.default_payment_method`.
 */
export interface StripeDefaultPaymentMethodSetupMetadata {
  setupIntentKind: "default_payment_method";
  teamId: string;
  initiatedByUserId: string;
}

export interface StripeWebhookCheckoutSession {
  id: string;
  customerId?: string;
  subscriptionId?: string;
  amountSubtotalCents?: number;
  amountTotalCents?: number;
  amountDiscountCents?: number;
  /** ISO 4217 currency code on the checkout session. */
  currency?: string | null;
  /**
   * Discounts applied on the session (e.g. a promotion code entered on
   * Stripe's hosted checkout page). Webhook payloads carry unexpanded IDs
   * only; coupon terms require a follow-up fetch.
   */
  discounts?: { couponId?: string; promotionCodeId?: string }[];
  metadata: Record<string, string | null | undefined>;
}

export interface StripeWebhookSubscription {
  id: string;
  status: string;
  customerId?: string;
  priceIds?: string[];
  /** Stripe `subscription.cancel_at_period_end`. */
  cancelAtPeriodEnd?: boolean;
  /** ISO 4217 currency of the subscription items, when available. */
  currency?: string | null;
  /** Sum of current items' `price.unit_amount * quantity`, when available. */
  unitAmountCents?: number;
  /**
   * Changed-field snapshot from `event.data.previous_attributes`, present only
   * on `customer.subscription.updated` events.
   */
  previousAttributes?: StripeWebhookSubscriptionPreviousAttributes;
  metadata: Record<string, string | null | undefined>;
}

export interface StripeWebhookRefund {
  id: string;
  chargeId: string;
  paymentIntentId?: string;
  amountCents: number;
  chargeAmountCents?: number;
  amountRefundedCents?: number;
  /** Invoice backing a subscription Charge, when Stripe includes it. */
  invoiceId?: string;
  metadata: Record<string, string | null | undefined>;
}

/** Normalized projection of `charge.dispute.created`. */
export interface StripeWebhookDispute {
  id: string;
  chargeId?: string;
  paymentIntentId?: string;
  amountCents: number;
  currency?: string | null;
  metadata: Record<string, string | null | undefined>;
}

export interface StripeWebhookInvoice {
  id: string;
  subscriptionId?: string;
  customerId?: string;
  status?: string | null;
  amountDueCents: number;
  amountPaidCents: number;
  attemptCount: number;
  hostedInvoiceUrl?: string | null;
  /**
   * Stripe `invoice.billing_reason`. Lets the webhook handler distinguish
   * a first-payment invoice (`subscription_create`) from a recurring
   * renewal (`subscription_cycle`). Empty/undefined when the event source
   * isn't `invoice.paid` (e.g. payment failure events).
   */
  billingReason?: string | null;
  /** ISO 4217 currency on the invoice (Stripe `invoice.currency`). */
  currency?: string | null;
  /** Sum of `invoice.total_discount_amounts[].amount` in cents. */
  amountDiscountCents?: number;
  /** Latest line item's price ID — used to map back to a subscription tier. */
  priceIds?: string[];
  /**
   * Catalog price lines with their signed invoice amounts. For a proration,
   * the previous plan is a negative line and the newly selected plan is the
   * positive line.
   */
  priceLineItems?: StripeWebhookInvoicePriceLineItem[];
  metadata: Record<string, string | null | undefined>;
  subscriptionMetadata: Record<string, string | null | undefined>;
}

export interface StripeWebhookInvoicePriceLineItem {
  priceId: string;
  amountCents: number;
  periodStart?: Date;
  periodEnd?: Date;
}

export interface StripeWebhookPaymentIntent {
  id: string;
  customerId?: string;
  amountCents: number;
  status: string;
  metadata: Record<string, string | null | undefined>;
  errorCode?: string;
  errorMessage?: string;
}

/**
 * Radar/outcome summary from Stripe `charge.outcome`. Only present on
 * `charge.succeeded` / `charge.failed` events, and every field is optional —
 * `risk_score` in particular requires Radar for Fraud Teams.
 */
export interface StripeWebhookChargeOutcome {
  networkStatus?: string | null;
  reason?: string | null;
  riskLevel?: string | null;
  riskScore?: number | null;
  sellerMessage?: string | null;
  type?: string | null;
}

/**
 * Normalized projection of a Stripe Charge from a `charge.succeeded` /
 * `charge.failed` webhook event. Charges (not PaymentIntents) carry the Radar
 * outcome and card payment-method details, which is why the Slack activity
 * feed posts payment success/failure from `charge.*` events.
 */
export interface StripeWebhookCharge {
  id: string;
  status: string;
  amountCents: number;
  currency?: string | null;
  paymentIntentId?: string;
  customerId?: string;
  /** `billing_details.email`, falling back to `receipt_email`. */
  billingEmail?: string | null;
  paymentMethodBrand?: string | null;
  paymentMethodLast4?: string | null;
  paymentMethodFunding?: string | null;
  failureCode?: string | null;
  failureMessage?: string | null;
  outcome?: StripeWebhookChargeOutcome;
  metadata: Record<string, string | null | undefined>;
}

/**
 * Normalized projection of a `payment_method.detached` webhook event.
 * GOTCHA: after detachment the PaymentMethod object's `customer` is null —
 * the customer id only survives on the event's `previous_attributes.customer`,
 * which is where the parser reads it from.
 */
export interface StripeWebhookPaymentMethodDetached {
  paymentMethodId: string;
  customerId?: string;
  brand?: string | null;
  last4?: string | null;
  funding?: string | null;
}

/**
 * Subset of `event.data.previous_attributes` for
 * `customer.subscription.updated` events, used to classify the update
 * (cancellation scheduled/reactivated, upgrade vs downgrade, activation)
 * without another Stripe fetch. Only the fields that actually changed are
 * present on the wire, so everything here is optional.
 */
export interface StripeWebhookSubscriptionPreviousAttributes {
  cancelAtPeriodEnd?: boolean;
  status?: string;
  priceIds?: string[];
  /** Sum of previous items' `price.unit_amount * quantity`, when available. */
  unitAmountCents?: number;
}

/**
 * Normalized projection of a Stripe SetupIntent from a `setup_intent.*` webhook
 * event. `paymentMethodId` is the payment method the SetupIntent collected,
 * which the handler promotes to the customer's default once it verifies the
 * intent belongs to the default-payment-method setup flow via `metadata`.
 */
export interface StripeWebhookSetupIntent {
  id: string;
  customerId?: string;
  paymentMethodId?: string;
  status: string;
  metadata: Record<string, string | null | undefined>;
}

export interface ProcessStripeWebhookEventInput {
  stripeEventId: string;
  eventType: string;
  livemode: boolean;
  payload: BillingJsonValue;
  checkoutSession?: StripeWebhookCheckoutSession;
  subscription?: StripeWebhookSubscription;
  refund?: StripeWebhookRefund;
  dispute?: StripeWebhookDispute;
  invoice?: StripeWebhookInvoice;
  paymentIntent?: StripeWebhookPaymentIntent;
  setupIntent?: StripeWebhookSetupIntent;
  charge?: StripeWebhookCharge;
  paymentMethodDetached?: StripeWebhookPaymentMethodDetached;
}

export interface ProcessStripeWebhookEventResult {
  event: StripeEvent;
  processed: boolean;
}

export interface SyncStripeEventsInput {
  limit?: number;
  createdAfter?: number;
}

export interface SyncedStripeEventResult {
  stripeEventId: string;
  eventType: string;
  status: StripeEventStatus;
  processed: boolean;
  summary?: BillingJsonValue;
  errorMessage?: string;
}

export interface SyncStripeEventsResult {
  checked: number;
  processed: number;
  skipped: number;
  failed: number;
  events: SyncedStripeEventResult[];
}

export interface GetTeamStripeCustomerIdInput {
  teamId: string;
  ctx?: TransactionContext;
}

export interface SetTeamStripeCustomerIdInput {
  teamId: string;
  stripeCustomerId: string;
  ctx?: TransactionContext;
}

export interface GetOrCreateStripeCustomerInput {
  teamId: string;
  initiatedByUserId?: string;
  customerEmail?: string;
  teamName?: string;
  teamSlug?: string;
}

export interface StripeCustomerRef {
  id: string;
}

export interface GetPostpaidEnablementReadinessInput {
  teamId: string;
}

/**
 * Snapshot of whether a team can be charged off-session for post-paid
 * collection today. Produced at the Stripe boundary and used as the
 * collectability gate before a team is enabled for post-paid billing.
 *
 * `ready` mirrors what scheduled collection actually requires: a Stripe
 * customer plus a default payment method (`invoice_settings.default_payment_method`),
 * which is what the off-session PaymentIntent charges.
 * `attachedPaymentMethodCount` is surfaced separately so operators can
 * distinguish "no card on file" from "card on file but not promoted to
 * default" (a known Stripe Checkout gap).
 */
export interface PostpaidEnablementReadiness {
  teamId: string;
  ready: boolean;
  hasStripeCustomer: boolean;
  stripeCustomerId: string | null;
  hasDefaultPaymentMethod: boolean;
  defaultPaymentMethodId: string | null;
  attachedPaymentMethodCount: number;
  checkedAt: Date;
}

/**
 * Admin request to change a team's post-paid eligibility: the domain input
 * minus `collectability`, which this boundary derives itself from live Stripe
 * readiness rather than accepting from callers.
 */
export type SetPostpaidBillingForTeamInput = Omit<
  SetPostpaidBillingInput,
  "collectability"
>;

export interface CreateStripeSubscriptionCheckoutSessionInput {
  teamId: string;
  initiatedByUserId: string;
  /** Canonical plan selector. New callers must provide this field. */
  targetPlan?: StripeSubscriptionPlanKey;
  /** Legacy Free/Growth selector retained while existing callers migrate. */
  targetTier?: StripeCatalogSubscriptionTier;
  successUrl: string;
  cancelUrl: string;
  customerEmail?: string;
  teamName?: string;
  teamSlug?: string;
  /**
   * One-time credit purchase (cents) included on a FREE-tier subscription
   * Checkout. When set, the credit line item and
   * `paygMandatoryCreditPurchaseAmountCents` metadata use this amount.
   */
  initialCreditAmountCents?: number;
  onboardingMetadata?: FastWebOnboardingCheckoutMetadata;
}

export interface CreateOutOfCreditsCheckoutSessionInput {
  teamId: string;
  initiatedByUserId: string;
  targetTier: StripeCatalogSubscriptionTier;
  amountCents?: number;
  successUrl: string;
  cancelUrl: string;
}

export interface OutOfCreditsCheckoutSessionResult {
  url: string;
}

/**
 * Starts the onboarding-specific subscription Checkout flow. Unlike a normal
 * billing-settings Checkout, this flow persists a durable attempt so the
 * browser can resume or finalize the exact session after an interruption.
 */
export type CreateOnboardingStripeSubscriptionCheckoutSessionInput =
  CreateStripeSubscriptionCheckoutSessionInput;

/**
 * Finalize a standard-subscription onboarding Checkout Session on browser
 * return: the server verifies and persists that exact session's subscription,
 * then promotes its captured payment method to the team default. Only the
 * Checkout Session id is trusted from the client.
 */
export interface CompleteCheckoutSetupInput {
  teamId: string;
  checkoutSessionId: string;
}

export interface CreateStripeCreditCheckoutSessionInput {
  teamId: string;
  initiatedByUserId: string;
  amountCents: number;
  discountCode?: string;
  /**
   * Save the Checkout payment method for future off-session charges. Callers
   * set this only after the customer has opted into automatic top-up.
   */
  savePaymentMethodForFutureUse?: boolean;
  successUrl: string;
  cancelUrl: string;
  customerEmail?: string;
  teamName?: string;
  teamSlug?: string;
  onboardingMetadata?: FastWebOnboardingCheckoutMetadata;
  onboardingCheckoutAttemptId?: string;
  checkoutIdempotencyKey?: string;
}

export interface FastWebOnboardingCheckoutMetadata {
  automaticOfferDecisionId: string;
  phoneVerifiedAtCheckout: boolean;
  continuity: OnboardingBillingContinuityInput;
}

export type OnboardingBillingFundingInput =
  | {
      kind: "payg";
      amountCents: number;
    }
  | {
      kind: "subscription";
      planKey: "operator" | "studio";
    };

export type OnboardingBillingContinuityInput =
  | {
      kind: "pause_when_empty";
    }
  | {
      kind: "keep_running";
      thresholdNanocredits: bigint;
      amountCents: number;
      monthlyLimitCents: number;
      consentVersion: FastWebAutoTopUpConsentVersion;
    };

export interface GetOnboardingBillingOfferInput {
  teamId: string;
}

export interface OnboardingBillingOffer {
  pricing: {
    paygMinimumCents: number;
    plans: {
      planKey: "operator" | "studio";
      priceCents: number;
      allowanceCents: number;
    }[];
  };
  phone: {
    number: string | null;
    verified: boolean;
  };
  welcomeOffer: {
    program: "fast_web_welcome_v1";
    amountCents: 500;
    status: AutomaticBillingOfferStatus | null;
  };
  defaults: {
    payg: OnboardingAutoTopUpDefaults;
    operator: OnboardingAutoTopUpDefaults;
    studio: OnboardingAutoTopUpDefaults;
  };
  hasPendingCheckout: boolean;
}

export interface OnboardingAutoTopUpDefaults {
  thresholdNanocredits: bigint;
  amountCents: number;
  monthlyLimitCents: number;
  consentVersion: FastWebAutoTopUpConsentVersion;
}

export interface CreateOnboardingBillingCheckoutInput {
  teamId: string;
  funding: OnboardingBillingFundingInput;
  continuity: OnboardingBillingContinuityInput;
  successUrl: string;
  cancelUrl: string;
  customerEmail?: string;
  teamName?: string;
  teamSlug?: string;
}

export interface GetOnboardingBillingStatusInput {
  teamId: string;
}

export interface OnboardingBillingStatus {
  state: "idle" | "pending" | "settled";
  fundingKind: "payg" | "subscription" | null;
  planKey: "operator" | "studio" | null;
  hasPaymentMethod: boolean;
  autoTopUpEnabled: boolean;
  welcomeOfferStatus: AutomaticBillingOfferStatus | null;
  gatewayAvailableNanocredits: bigint;
}

export interface CreateStripePortalSessionInput {
  teamId: string;
  returnUrl: string;
  customerEmail?: string;
  teamName?: string;
  teamSlug?: string;
}

export interface CreateDefaultPaymentMethodSetupIntentInput {
  teamId: string;
  initiatedByUserId: string;
  customerEmail?: string;
  teamName?: string;
  teamSlug?: string;
}

/**
 * Result of creating a SetupIntent for in-app default-payment-method capture.
 * `clientSecret` is handed to Stripe.js so the browser can confirm the
 * SetupIntent with the entered card; the server never sees raw card data.
 */
export interface CreateDefaultPaymentMethodSetupIntentResult {
  setupIntentId: string;
  clientSecret: string;
  customerId: string;
}

/**
 * Server-side completion of the in-app setup flow. The client sends back the
 * `setupIntentId` it confirmed; the service re-reads it from Stripe (never
 * trusting the client) before promoting the collected payment method to the
 * team's default. Returns the refreshed {@link TeamPaymentMethodStatus}.
 */
export interface CompleteDefaultPaymentMethodSetupInput {
  teamId: string;
  setupIntentId: string;
}

export type StripeSubscriptionPortalFlow =
  | {
      kind: "cancel";
    }
  | {
      kind: "update";
      targetTier: StripeCatalogSubscriptionTier;
    };

export interface CreateStripeSubscriptionPortalSessionInput {
  teamId: string;
  returnUrl: string;
  flow: StripeSubscriptionPortalFlow;
  customerEmail?: string;
  teamName?: string;
  teamSlug?: string;
}

export interface GetStripeSubscriptionManagementDetailInput {
  teamId: string;
}

export interface StripeSubscriptionManagementDetail {
  id: string;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPlan: StripeSubscriptionPlanKey | null;
  pendingPlan: StripeSubscriptionPlanKey | null;
  pendingPlanEffectiveAt: Date | null;
  currentPeriodStart: Date | null;
  currentPeriodEnd: Date | null;
  scheduleId: string | null;
}

export interface ScheduleStripeSubscriptionPlanChangeInput {
  teamId: string;
  targetPlan: StripeSubscriptionPlanKey;
}

export interface SetStripeSubscriptionCancelAtPeriodEndInput {
  teamId: string;
  cancelAtPeriodEnd: boolean;
}

/**
 * Server-side billing proof required before an incomplete team may be marked
 * onboarded. This deliberately bypasses the payment-method status cache: a
 * cached answer is suitable for UI display, but not for completing onboarding.
 */
export interface GetOnboardingBillingCompletionStateInput {
  teamId: string;
}

export interface OnboardingBillingCompletionState {
  hasDefaultPaymentMethod: boolean;
  /**
   * Canonical, fresh Stripe proof that this team can safely complete
   * onboarding. Consumers must use this rather than re-implementing the
   * metadata/customer/status checks from UI cache state.
   */
  isOnboardingBillingCompletionReady: boolean;
  isOnboardingSubscription: boolean;
  subscriptionCustomerMatchesTeam: boolean;
  subscriptionStatus: string | null;
}

export interface ResumePendingOnboardingCheckoutInput {
  teamId: string;
}

/**
 * Server-side reconciliation of the Checkout session recorded before a
 * browser leaves for Stripe. The session itself remains the authority for its
 * current lifecycle state and for browser-return finalization.
 */
export type ResumePendingOnboardingCheckoutResult =
  | { status: "completed" | "expired" | "none" }
  | { status: "open"; checkoutUrl: string };

export interface GetTeamPaymentMethodStatusInput {
  teamId: string;
  /**
   * Force a fresh Stripe read instead of serving the cached value. The forced
   * read is itself rate-limited (at most once per minute per team) so a tight
   * client retry loop cannot turn into a Stripe API hammer.
   */
  forceRefresh?: boolean;
}

/**
 * Whether a team has a default payment method on file, with enough provenance
 * for callers (web billing guards, ingest enforcement) to decide how much to
 * trust the answer.
 *
 * `hasDefaultPaymentMethod` is true iff the team's Stripe customer has
 * `invoice_settings.default_payment_method` set (strict-default semantics).
 * Note this is deliberately narrower than "has any chargeable PM attached":
 * Stripe Checkout attaches a card without promoting it to the default, so a
 * team that has only ever checked out reads false until something promotes it.
 */
export interface TeamPaymentMethodStatus {
  teamId: string;
  hasDefaultPaymentMethod: boolean;
  /**
   * True when the value was read from Stripe on this call; false when served
   * from cache (fresh or stale). Lets a caller distinguish a just-confirmed
   * answer from a possibly-out-of-date one.
   */
  fresh: boolean;
  /**
   * Epoch milliseconds of the underlying Stripe read this value reflects. Null
   * when the answer did not come from a Stripe read (no customer on file, or a
   * degraded fail-open with no prior cached value).
   */
  checkedAtEpochMs: number | null;
  /**
   * True when Stripe could not be reached and no usable cached value existed,
   * so we fell open to `hasDefaultPaymentMethod: false`. Callers MUST NOT treat
   * a degraded result as a hard "no payment method" — a Stripe outage should
   * not block a team that may well have a card on file.
   */
  degraded: boolean;
}

export interface GetDefaultPaymentMethodSummaryInput {
  teamId: string;
}

/**
 * Display summary of a team's default payment method, used to render the
 * billing UI (e.g. "Visa •••• 4242" or "Bank of America ••••6789"). A `null`
 * result means no default payment method is on file.
 *
 * `card` is populated for card payment methods and `usBankAccount` for ACH/bank
 * payment methods; both are `null` for any other payment-method type, which
 * `type` still identifies. Brand/last4 are nullable because Stripe does not
 * guarantee them for every payment method, and the UI degrades to showing just
 * the type when they are absent.
 */
export interface DefaultPaymentMethodSummary {
  id: string;
  type: string;
  card: {
    brand: string | null;
    last4: string | null;
    expMonth: number | null;
    expYear: number | null;
  } | null;
  usBankAccount: {
    bankName: string | null;
    last4: string | null;
  } | null;
}

export type ExecuteStripeAutoTopUpStatus =
  | "charged"
  | "continued"
  | "disabled"
  | "missing_customer"
  | "missing_default_payment_method"
  | "pending_payment"
  | "skipped_balance_above_threshold"
  | "skipped_cooldown"
  | "skipped_failure_backoff"
  | "skipped_monthly_limit"
  | "skipped_locked";

export const STRIPE_AUTO_TOP_UP_EXECUTION_STATUSES = {
  FAILED: "failed",
  PAYMENT_INTENT_CREATED: "payment_intent_created",
  STARTED: "started",
  SUCCEEDED: "succeeded",
} as const;

export type StripeAutoTopUpExecutionStatus =
  (typeof STRIPE_AUTO_TOP_UP_EXECUTION_STATUSES)[keyof typeof STRIPE_AUTO_TOP_UP_EXECUTION_STATUSES];

/** Executions that already reserve a monthly auto top-up cap. */
export const STRIPE_AUTO_TOP_UP_MONTHLY_CAP_STATUSES = [
  STRIPE_AUTO_TOP_UP_EXECUTION_STATUSES.PAYMENT_INTENT_CREATED,
  STRIPE_AUTO_TOP_UP_EXECUTION_STATUSES.SUCCEEDED,
] as const;

export const LEGACY_AUTO_TOP_UP_CONSENT_VERSION = "legacy_billing_settings_v1";
export const LEGACY_AUTO_TOP_UP_MINIMUM_MONTHLY_LIMIT_CENTS = 5_000;

/**
 * Max monthly auto top-up cap a team may configure. Configurable via
 * AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS (Infisical) so it can be raised without
 * a deploy; a missing or malformed value falls back to
 * {@link AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS_DEFAULT} ($50,000).
 */
export const AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS_ENV =
  "AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS";
export const AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS_DEFAULT = 5_000_000;

export function resolveAutoTopUpMonthlyLimitMaxCents(
  raw: string | null | undefined,
): number {
  if (raw == null || raw.trim() === "") {
    return AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS_DEFAULT;
  }
  const parsed = Number(raw.trim());
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS_DEFAULT;
  }
  return parsed;
}

/** Resolved once at module load; `process` may not exist in browser bundles. */
export const AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS =
  resolveAutoTopUpMonthlyLimitMaxCents(
    typeof process !== "undefined"
      ? process.env[AUTO_TOP_UP_MONTHLY_LIMIT_MAX_CENTS_ENV]
      : undefined,
  );

export type CollectPostpaidInvoiceStatus =
  | "charged"
  | "already_paid"
  | "missing_customer"
  | "missing_default_payment_method"
  | "not_collectible"
  | "payment_failed"
  | "skipped_off_platform"
  | "skipped_zero_due";

export interface StripeAutoTopUpSettingsConfiguration {
  /** Team whose billing-v2 credit ledger should be monitored for refill. */
  teamId: string;
  /**
   * Whether automatic refill is active. Disabled settings keep amount and
   * threshold nullable so turning the feature off cannot accidentally charge.
   */
  enabled: boolean;
  /** Stripe off-session PaymentIntent amount, stored in USD cents. */
  amountCents: number | null;
  /** Maximum automatic charges allowed during the current UTC month. */
  monthlyLimitCents: number | null;
  /** Versioned auto top-up terms accepted by the customer. */
  consentVersion: string | null;
  /**
   * Exact credit ledger threshold in nanocredits. Execution charges only when
   * the ledger-derived balance is below this value.
   */
  thresholdNanocredits: bigint | null;
  /** Last settings write time, or null when the team has never configured it. */
  updatedAt: Date | null;
}

export interface StripeAutoTopUpSettings extends StripeAutoTopUpSettingsConfiguration {
  /** Pending and settled auto top-up charges in the current UTC month. */
  currentMonthChargedAndPendingCents: number;
  /** Remaining automatic-charge capacity for the current UTC month. */
  monthlyLimitRemainingCents: number | null;
  /** Start of the next UTC month, when the monthly cap resets. */
  monthlyLimitResetsAt: Date;
}

export interface StripeAutoTopUpExecution {
  /** Durable execution id used in Stripe metadata and idempotency keys. */
  id: string;
  /** Team being evaluated for an off-session refill. */
  teamId: string;
  /** Stable key passed to Stripe so retries cannot create duplicate charges. */
  idempotencyKey: string;
  /** Amount charged by this execution in USD cents. */
  amountCents: number;
  /** Threshold that caused this execution to fire, captured for auditability. */
  thresholdNanocredits: bigint;
  /**
   * Ledger balance observed immediately before creating the PaymentIntent.
   * This is diagnostic state; credit_transactions remains the source of truth.
   */
  balanceNanocreditsAtTrigger: bigint;
  /** Stripe PaymentIntent id once the off-session charge has been created. */
  stripePaymentIntentId?: string;
  /**
   * Payment method this execution charges, persisted at reservation so a
   * resumed execution replays identical Stripe parameters. Absent on rows
   * created before the column existed.
   */
  stripePaymentMethodId?: string;
  /** How stripePaymentMethodId was resolved; sent as PaymentIntent metadata. */
  stripePaymentMethodSource?: string;
  /** Time the PaymentIntent was created for UTC cap accounting. */
  paymentIntentCreatedAt?: Date;
  /** Operational lifecycle state for duplicate-charge protection. */
  status: StripeAutoTopUpExecutionStatus;
  /** Last Stripe failure message when the execution reaches failed state. */
  errorMessage?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface GetStripeAutoTopUpSettingsInput {
  teamId: string;
}

export interface UpdateStripeAutoTopUpSettingsInput {
  teamId: string;
  enabled: boolean;
  amountCents: number | null;
  thresholdNanocredits: bigint | null;
  monthlyLimitCents: number | null;
  consentVersion: string | null;
}

export interface ListEnabledStripeAutoTopUpSettingsInput {
  limit?: number;
}

export interface ExecuteStripeAutoTopUpInput {
  teamId: string;
}

export interface ExecuteStripeAutoTopUpResult {
  teamId: string;
  status: ExecuteStripeAutoTopUpStatus;
  balanceNanocredits: bigint;
  amountCents: number | null;
  thresholdNanocredits: bigint | null;
  stripePaymentIntentId?: string;
  autoTopUpExecutionId?: string;
}

export interface CollectPostpaidInvoiceInput {
  invoiceId: string;
}

export interface RetryPostpaidInvoicePaymentInput {
  invoiceId: string;
}

export interface FinalizeAndCollectPostpaidInvoicesInput {
  teamId: string;
  periodStart: Date;
  periodEnd: Date;
  dueAt: Date;
  collectionTrigger: string;
}

export type PostpaidInvoiceCollectionOutcome =
  | "settled_by_credits"
  | "collection_attempted"
  | "collection_failed";

export interface CollectedPostpaidInvoice {
  invoiceId: string;
  outcome: PostpaidInvoiceCollectionOutcome;
  collection?: CollectPostpaidInvoiceResult;
  errorMessage?: string;
}

export interface CollectPostpaidInvoiceResult {
  invoiceId: string;
  teamId: string;
  status: CollectPostpaidInvoiceStatus;
  amountCents: number | null;
  amountNanocredits: bigint;
  stripePaymentIntentId?: string;
  paymentAttemptId?: string;
  errorMessage?: string;
}

export interface StripeCheckoutSessionResult {
  id: string;
  url: string;
  customerId: string;
  paygMandatoryCreditPurchaseAmountCents?: number;
}

export interface StripePortalSessionResult {
  id: string;
  url: string;
  customerId: string;
}

export interface StripeReceiptUrlResult {
  url: string | null;
  unavailableReason?: string;
}

export interface PaygCheckoutRequirements {
  mandatoryCreditPurchaseAmountCents: number | null;
}

export interface IStripeBillingService {
  getPaygCheckoutRequirements(): PaygCheckoutRequirements;

  getOnboardingBillingOffer(params: {
    input: GetOnboardingBillingOfferInput;
  }): Promise<OnboardingBillingOffer>;

  createOnboardingBillingCheckout(params: {
    input: CreateOnboardingBillingCheckoutInput;
  }): Promise<StripeCheckoutSessionResult>;

  getOnboardingBillingStatus(params: {
    input: GetOnboardingBillingStatusInput;
  }): Promise<OnboardingBillingStatus>;

  /**
   * Return the existing Stripe customer for a team or create one from
   * app-provided identity data. Billing-service owns Stripe customer linkage;
   * callers pass config and user/team display data without reading env vars.
   */
  getOrCreateCustomer(params: {
    input: GetOrCreateStripeCustomerInput;
  }): Promise<StripeCustomerRef>;

  /**
   * Resolve whether a team is collectable through Stripe off-session, used as
   * the preflight gate before enabling post-paid billing. Read-only: it never
   * creates a customer or mutates payment-method state.
   */
  getPostpaidEnablementReadiness(params: {
    input: GetPostpaidEnablementReadinessInput;
  }): Promise<PostpaidEnablementReadiness>;

  /**
   * Set a team's post-paid eligibility, owning the collectability gate: when
   * newly enabling without an override, live Stripe readiness is captured
   * here and translated into the provider-neutral facts the billing policy
   * consumes. A failed readiness fetch degrades to "no facts", so fresh
   * enables get a clear not-ready rejection while re-saves of
   * already-enabled teams and the override path proceed during a Stripe
   * outage.
   */
  setPostpaidBillingForTeam(params: {
    input: SetPostpaidBillingForTeamInput;
  }): Promise<EffectiveSubscription>;

  /**
   * Finalize a collection candidate's uninvoiced usage and collect each
   * resulting invoice, owning the settlement policy: invoices fully covered
   * by applied credits are reported as settled instead of charged, and a
   * failed charge is isolated per invoice. Returns one outcome per invoice
   * so schedulers only iterate and log.
   */
  finalizeAndCollectPostpaidInvoices(params: {
    input: FinalizeAndCollectPostpaidInvoicesInput;
  }): Promise<CollectedPostpaidInvoice[]>;

  createSubscriptionCheckoutSession(params: {
    input: CreateStripeSubscriptionCheckoutSessionInput;
  }): Promise<StripeCheckoutSessionResult>;

  createOutOfCreditsCheckoutSession(params: {
    input: CreateOutOfCreditsCheckoutSessionInput;
  }): Promise<OutOfCreditsCheckoutSessionResult>;

  createOnboardingSubscriptionCheckoutSession(params: {
    input: CreateOnboardingStripeSubscriptionCheckoutSessionInput;
  }): Promise<StripeCheckoutSessionResult>;

  /**
   * Finalize an onboarding standard-subscription Checkout Session on browser
   * return: persist the verified session subscription, promote its captured
   * payment method to the team default, and return the refreshed status.
   */
  completeCheckoutSetup(params: {
    input: CompleteCheckoutSetupInput;
  }): Promise<TeamPaymentMethodStatus>;

  createCreditCheckoutSession(params: {
    input: CreateStripeCreditCheckoutSessionInput;
  }): Promise<StripeCheckoutSessionResult>;

  createPortalSession(params: {
    input: CreateStripePortalSessionInput;
  }): Promise<StripePortalSessionResult>;

  createSubscriptionPortalSession(params: {
    input: CreateStripeSubscriptionPortalSessionInput;
  }): Promise<StripePortalSessionResult>;

  getSubscriptionManagementDetail(params: {
    input: GetStripeSubscriptionManagementDetailInput;
  }): Promise<StripeSubscriptionManagementDetail | null>;

  /** Schedule a commercial plan change at the next paid period boundary. */
  scheduleSubscriptionPlanChange(params: {
    input: ScheduleStripeSubscriptionPlanChangeInput;
  }): Promise<StripeSubscriptionManagementDetail>;

  /** Remove a not-yet-effective plan change and keep the current plan. */
  clearScheduledSubscriptionPlanChange(params: {
    input: GetStripeSubscriptionManagementDetailInput;
  }): Promise<StripeSubscriptionManagementDetail>;

  /** Schedule or undo cancellation at the current paid period boundary. */
  setSubscriptionCancelAtPeriodEnd(params: {
    input: SetStripeSubscriptionCancelAtPeriodEndInput;
  }): Promise<StripeSubscriptionManagementDetail>;

  /**
   * Re-read the Stripe customer and persisted subscription needed to complete
   * onboarding. The implementation must fail rather than use a cached payment
   * method answer when Stripe cannot be reached.
   */
  getOnboardingBillingCompletionState(params: {
    input: GetOnboardingBillingCompletionStateInput;
  }): Promise<OnboardingBillingCompletionState>;

  clearPendingOnboardingCheckoutForTeam(teamId: string): Promise<void>;

  /**
   * Resolve the exact Checkout session recorded for a team before redirecting
   * away from the app. This closes the browser-return/webhook timing window
   * without discovering arbitrary subscriptions from the Stripe customer.
   */
  resumePendingOnboardingCheckout(params: {
    input: ResumePendingOnboardingCheckoutInput;
  }): Promise<ResumePendingOnboardingCheckoutResult>;

  /**
   * Report whether a team has a default payment method on file. Backed by a
   * stale-while-revalidate cache (fresh window minutes long, forced refresh
   * rate-limited) so hot enforcement paths can call it cheaply. Fails open to
   * `hasDefaultPaymentMethod: false, degraded: true` when Stripe is unreachable
   * and no cached value exists.
   */
  getTeamPaymentMethodStatus(params: {
    input: GetTeamPaymentMethodStatusInput;
  }): Promise<TeamPaymentMethodStatus>;

  /**
   * Return a display summary (card brand/last4 or bank name/last4) of the
   * team's default payment method for the billing UI, or `null` when none is on
   * file. Always reads fresh from Stripe — meant for the low-frequency settings
   * page, not the hot enforcement path served by
   * {@link getTeamPaymentMethodStatus}.
   */
  getDefaultPaymentMethodSummary(params: {
    input: GetDefaultPaymentMethodSummaryInput;
  }): Promise<DefaultPaymentMethodSummary | null>;

  /**
   * Recompute the team's effective subscription tier directly from Stripe and
   * persist it, bypassing the webhook. Backs the user-driven "Refresh billing"
   * action so a delayed/missed/out-of-order subscription webhook can't leave
   * the team stuck on a stale tier.
   */
  reconcileSubscriptionFromStripe(params: { teamId: string }): Promise<{
    tier: SubscriptionTier;
    planKey: StripeSubscriptionPlanKey | null;
    stripeSubscriptionId: string | null;
  }>;

  /**
   * Admin unstick (INF-4110): cancel the team's Stripe subscription
   * (tolerating already-canceled/missing subscriptions) and reconcile from
   * Stripe so `stripe_subscription_id` is cleared and the team can re-enroll
   * through checkout. A subscription row created by a checkout that never
   * yielded a usable default payment method otherwise blocks re-checkout via
   * the duplicate-checkout guard.
   */
  clearStripeSubscriptionForTeam(params: {
    input: { teamId: string };
  }): Promise<{
    previousStripeSubscriptionId: string | null;
    canceled: boolean;
    tier: SubscriptionTier;
    planKey: StripeSubscriptionPlanKey | null;
    stripeSubscriptionId: string | null;
  }>;

  /**
   * Create a Stripe SetupIntent so a team can add a default payment method
   * in-app. Gets or creates the team's Stripe customer and returns the client
   * secret for Stripe.js to confirm the card off-session.
   */
  createDefaultPaymentMethodSetupIntent(params: {
    input: CreateDefaultPaymentMethodSetupIntentInput;
  }): Promise<CreateDefaultPaymentMethodSetupIntentResult>;

  /**
   * Finalize the in-app setup flow: re-read the confirmed SetupIntent from
   * Stripe, verify it belongs to this team's customer and default-payment
   * method flow, promote the collected payment method to the customer default,
   * and return the refreshed payment-method status.
   */
  completeDefaultPaymentMethodSetup(params: {
    input: CompleteDefaultPaymentMethodSetupInput;
  }): Promise<TeamPaymentMethodStatus>;

  /**
   * Read automatic top-up settings from billing-owned subscription state.
   * Missing rows are returned as disabled settings, not as errors.
   */
  getAutoTopUpSettings(params: {
    input: GetStripeAutoTopUpSettingsInput;
  }): Promise<StripeAutoTopUpSettings>;

  /**
   * Validate and persist automatic top-up settings. The service enforces
   * min/max credit purchase amounts and threshold semantics before the store
   * writes normalized cents/nanocredits.
   */
  updateAutoTopUpSettings(params: {
    input: UpdateStripeAutoTopUpSettingsInput;
  }): Promise<StripeAutoTopUpSettings>;

  /**
   * Return enabled automatic top-up settings for cron fan-out. Scheduling is
   * caller-owned, but query semantics stay centralized in billing-service.
   */
  listEnabledAutoTopUpSettings(params: {
    input: ListEnabledStripeAutoTopUpSettingsInput;
  }): Promise<StripeAutoTopUpSettingsConfiguration[]>;

  /**
   * Evaluate and, when needed, create one team's off-session refill under the
   * configured distributed lock. Ledger deposits are intentionally deferred to
   * the Stripe webhook/event-sync path so balances stay append-only.
   */
  executeAutoTopUpForTeam(params: {
    input: ExecuteStripeAutoTopUpInput;
  }): Promise<ExecuteStripeAutoTopUpResult>;

  collectPostpaidInvoice(params: {
    input: CollectPostpaidInvoiceInput;
  }): Promise<CollectPostpaidInvoiceResult>;

  retryPostpaidInvoicePayment(params: {
    input: RetryPostpaidInvoicePaymentInput;
  }): Promise<CollectPostpaidInvoiceResult>;

  getCheckoutSessionReceiptUrl(params: {
    checkoutSessionId: string;
  }): Promise<StripeReceiptUrlResult>;

  getPaymentIntentReceiptUrl(params: {
    paymentIntentId: string;
  }): Promise<StripeReceiptUrlResult>;
}

export interface IStripeWebhookHandlerService {
  processEvent(params: {
    input: ProcessStripeWebhookEventInput;
  }): Promise<ProcessStripeWebhookEventResult>;
}

export interface IStripeEventSyncService {
  syncCheckoutSessionCompletedEvents(params: {
    input: SyncStripeEventsInput;
  }): Promise<SyncStripeEventsResult>;
}
